// A page-agnostic overlay that turns any layout into an editable page.
// Consumers pass a `layoutKey` (e.g. "festival:tacoma-santa-parade-2026"
// or "hub:community") and the renderer. This overlay handles:
//   • edit-mode toggle
//   • drag-to-reorder blocks
//   • add block (from BLOCK_REGISTRY)
//   • edit block fields (dialog)
//   • delete block

import { useEffect, useState, useCallback, useRef } from "react";
import { useLanguage, TranslationNotice } from "../../i18n/LanguageContext";
import { TranslateControl } from "./TranslateControl";
import { TtsProvider } from "../ListenButton";
import axios from "axios";
import { toast } from "sonner";
import { Move, Plus, X, GripVertical, Pencil, Check, ImageUp, Loader2, Lock } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { BLOCK_REGISTRY, BLOCK_TYPE_KEYS } from "../blocks/registry";
import EditorSheet from "./EditorSheet";
import BlockEditor from "./BlockEditor";
import NorthStarGuideBlock from "../blocks/NorthStarGuideBlock";
import { canEditLayout } from "../../utils/canEditLayout";

const opts = { withCredentials: true };
const AUTO_NORTH_STAR = { block_id: "auto-north-star", type: "north_star_guide", config: {} };

const SortableWrap = ({ blockId, editing, children, onEdit, onDelete, label, locked }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: blockId, disabled: !editing });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position: "relative" };
  if (!editing) return <div>{children}</div>;
  return (
    <div ref={setNodeRef} style={style} className="ring-2 ring-dashed ring-[#E76F51]/50 rounded-lg my-2">
      <div className="sticky top-14 z-40 flex items-center gap-2 bg-[#E76F51] text-white px-3 py-1.5 rounded-t-md shadow"
        {...attributes} {...listeners} style={{ cursor: "grab", touchAction: "none" }}>
        <GripVertical className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
        {locked ? (
          <span className="ml-auto inline-flex items-center gap-1 bg-white/20 rounded px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`block-chip-locked-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <Lock className="h-3 w-3" /> Kept by THE WALL
          </span>
        ) : (
        <span className="ml-auto flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} onPointerDown={(e) => e.stopPropagation()}
            className="bg-white text-[#E76F51] rounded px-2 py-0.5 text-[10px] font-bold uppercase inline-flex items-center gap-1" data-testid={`block-chip-edit-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <Pencil className="h-3 w-3" /> Edit
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} onPointerDown={(e) => e.stopPropagation()}
            className="bg-white text-[#C0392B] rounded px-2 py-0.5 text-[10px] font-bold uppercase inline-flex items-center gap-1">
            <X className="h-3 w-3" /> Delete
          </button>
        </span>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
};

export const PageSectionEditor = ({
  layoutKey,
  festival,
  renderExtraHiddenBlock,   // optional (block) => can render admin-only affordance for hidden blocks
  emptyMessage = "No blocks yet — click Add a block to start.",
  fallbackBlocks = null,     // optional bundled blocks shown when the DB has none for this key
}) => {
  const { user } = useAuth();
  const isAdmin = canEditLayout(user, layoutKey);
  const isSiteAdmin = user?.role === "admin";
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [swappingFlyer, setSwappingFlyer] = useState(false);
  const flyerInputRef = useRef(null);

  // One-click flyer swap: upload → new image becomes the hero + first gallery image.
  const replaceFlyer = async (file) => {
    if (!file) return;
    setSwappingFlyer(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/upload`, fd, opts);
      const url = up.data.url;
      const hero = blocks.find((b) => b.type === "hero");
      const gallery = blocks.find((b) => b.type === "photo_gallery");
      const calls = [];
      if (hero) calls.push(axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${hero.block_id}`, { config: { ...(hero.config || {}), hero_image: url } }, opts));
      if (gallery) {
        const imgs = [url, ...((gallery.config?.images || []).filter((u) => u !== url))];
        calls.push(axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${gallery.block_id}`, { config: { ...(gallery.config || {}), images: imgs } }, opts));
      }
      if (!calls.length) {
        calls.push(axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks`, { type: "photo_gallery", title: "The flyer", quip: "", config: { images: [url], columns: 2 } }, opts));
      }
      await Promise.all(calls);
      await load();
      toast.success("New flyer is live — header and gallery updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload the flyer");
    } finally {
      setSwappingFlyer(false);
      if (flyerInputRef.current) flyerInputRef.current.value = "";
    }
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const { lang } = useLanguage();
  const [translation, setTranslation] = useState(null);
  const load = useCallback(async () => {
    try {
      // Admins load the full layout (hidden blocks included) so they can toggle visibility; a chosen language loads the translated copy.
      const url = isAdmin && (editing || lang === "en")
        ? `${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}`
        : `${API}/page-layouts/${encodeURIComponent(layoutKey)}${lang !== "en" ? `?lang=${encodeURIComponent(lang)}` : ""}`;
      const r = await axios.get(url, opts);
      setTranslation(r.data.translation || null);
      let list = r.data.blocks || [];
      if (!list.length && fallbackBlocks?.length) list = fallbackBlocks;
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      setBlocks(list);
    } catch (e) {
      if (fallbackBlocks?.length) setBlocks([...fallbackBlocks].sort((a, b) => (a.order || 0) - (b.order || 0)));
    }
  }, [layoutKey, isAdmin, fallbackBlocks, lang, editing]);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.block_id === active.id);
    const newIdx = blocks.findIndex((b) => b.block_id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(blocks, oldIdx, newIdx);
    setBlocks(next);
    try {
      await axios.put(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/order`,
        { block_ids: next.map((b) => b.block_id) }, opts);
      toast.success("Order saved");
    } catch { toast.error("Couldn't save order"); load(); }
  };

  const addBlock = async (type) => {
    setBusy(true);
    try {
      const meta = BLOCK_REGISTRY[type];
      const r = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks`,
        { type, title: "", quip: "", config: meta.defaultConfig || {} }, opts);
      setBlocks([...blocks, r.data]);
      setShowAdd(false);
      setEditingBlock(r.data);
      toast.success(`${meta.label.split(" —")[0]} added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add block");
    } finally { setBusy(false); }
  };

  const deleteBlock = async (block_id) => {
    if (!window.confirm("Delete this block?")) return;
    try {
      await axios.delete(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${block_id}`, opts);
      setBlocks(blocks.filter((b) => b.block_id !== block_id));
      toast.success("Block deleted");
    } catch { toast.error("Couldn't delete"); }
  };

  const closeEditor = useCallback(() => { setEditingBlock(null); load(); }, [load]);

  const saveBlock = async (next) => {
    try {
      const r = await axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${next.block_id}`,
        { title: next.title, quip: next.quip, config: next.config,
          visible: next.visible, collapsed_by_default: next.collapsed_by_default,
          in_mini_preview: next.in_mini_preview }, opts);
      setBlocks(blocks.map((b) => b.block_id === next.block_id ? r.data : b));
    } catch { toast.error("Save failed"); }
  };

  const visibleBlocks = isAdmin && editing ? blocks : blocks.filter((b) => b.visible !== false);
  const hiddenCount = blocks.filter((b) => b.visible === false).length;
  const autoNorthStar = layoutKey.startsWith("festival:") && !blocks.some((x) => x.type === "north_star_guide");

  return (
    <>
      {/* Admin strip — top of page, always visible to people who can edit */}
      {isAdmin && (
        <div className="bg-[#1A1A1A] text-white text-sm" data-testid="page-editor-topbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 font-bold uppercase tracking-widest text-amber-300 text-xs"><Pencil className="h-3.5 w-3.5" /> You can edit this page</span>
            <span className="text-white/70 text-xs hidden sm:inline">
              {editing ? "Use the orange EDIT chips on each section. Changes save instantly — no republish needed." : "Change text, swap the flyer, add or reorder sections. Live the moment you save."}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {(isSiteAdmin || user?.role === "editor") && <TranslateControl layoutKey={layoutKey} onDone={load} />}
              <input ref={flyerInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => replaceFlyer(e.target.files?.[0])} data-testid="page-editor-flyer-input" />
              <Button size="sm" variant="outline" disabled={swappingFlyer} onClick={() => flyerInputRef.current?.click()} className="h-8 rounded-full text-xs font-bold border-white/40 bg-white/10 text-white hover:bg-white/20" data-testid="page-editor-replace-flyer">
                {swappingFlyer ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ImageUp className="h-3.5 w-3.5 mr-1" />} {swappingFlyer ? "Uploading…" : festival?.kind === "business" ? "Replace cover photo" : "Replace flyer"}
              </Button>
              {editing && (
                <Button size="sm" onClick={() => setShowAdd(true)} className="h-8 rounded-full bg-[#E76F51] hover:bg-[#d65f42] text-white text-xs font-bold" data-testid="page-editor-add-block-top">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add a block
                </Button>
              )}
              <Button size="sm" onClick={() => setEditing(!editing)} className={`h-8 rounded-full text-xs font-bold ${editing ? "bg-[#2E5C55] hover:bg-[#254b45] text-white" : "bg-amber-400 hover:bg-amber-500 text-amber-950"}`} data-testid="page-editor-toggle-top">
                {editing ? <><Check className="h-3.5 w-3.5 mr-1" /> Done editing</> : <><Pencil className="h-3.5 w-3.5 mr-1" /> Edit this page</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Public / non-editing render */}
      {!editing && (
        <TtsProvider layoutKey={layoutKey}>
        <div>
          {translation && <TranslationNotice meta={translation} />}
          {visibleBlocks.length === 0 ? (
            isAdmin ? <p className="max-w-2xl mx-auto px-6 py-10 text-center italic text-[#5C5C5C]">{emptyMessage}</p> : null
          ) : visibleBlocks.map((b, i) => {
            const meta = BLOCK_REGISTRY[b.type];
            if (!meta) return null;
            // A block with config.pair_with_next renders side by side with the block after it (lg+).
            const prev = visibleBlocks[i - 1];
            if (prev && prev.config?.pair_with_next && BLOCK_REGISTRY[prev.type]) return null;
            const Comp = meta.Render;
            const partner = b.config?.pair_with_next ? visibleBlocks[i + 1] : null;
            if (partner && BLOCK_REGISTRY[partner.type]) {
              const PComp = BLOCK_REGISTRY[partner.type].Render;
              return (
                <div key={b.block_id} className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-10" data-testid="block-pair">
                  <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
                    <Comp block={{ ...b, config: { ...b.config, paired: true } }} festival={festival} layoutKey={layoutKey} />
                    <PComp block={{ ...partner, config: { ...partner.config, paired: true } }} festival={festival} layoutKey={layoutKey} />
                  </div>
                </div>
              );
            }
            // Every Festival-Builder page gets the North Star spiking guide right under its hero,
            // unless the layout places one explicitly (admins add/hide a "North Star Guide" block to move or remove it).
            const autoGuide = autoNorthStar && b.type === "hero" && visibleBlocks.findIndex((x) => x.type === "hero") === i;
            return (
              <div key={b.block_id}>
                <Comp block={b} festival={festival} layoutKey={layoutKey} />
                {autoGuide && <NorthStarGuideBlock block={AUTO_NORTH_STAR} layoutKey={layoutKey} />}
              </div>
            );
          })}
        </div>
        </TtsProvider>
      )}

      {/* Admin edit render (drag / labels / add / edit / delete) */}
      {editing && (
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={blocks.map((b) => b.block_id)} strategy={verticalListSortingStrategy}>
              {blocks.map((b) => {
                const meta = BLOCK_REGISTRY[b.type];
                const Comp = meta?.Render;
                return (
                  <SortableWrap
                    key={b.block_id}
                    blockId={b.block_id}
                    editing={editing}
                    label={`${meta?.label?.split(" —")[0] || b.type}${b.visible === false ? " · HIDDEN" : ""}`}
                    onEdit={() => setEditingBlock(b)}
                    onDelete={() => deleteBlock(b.block_id)}
                    locked={Boolean(b.locked) && !isSiteAdmin}
                  >
                    {Comp && <Comp block={b} festival={festival} layoutKey={layoutKey} />}
                    {b.visible === false && renderExtraHiddenBlock && renderExtraHiddenBlock(b)}
                  </SortableWrap>
                );
              })}
            </SortableContext>
          </DndContext>
          {blocks.length === 0 && (
            <p className="text-center italic text-[#5C5C5C] py-10">Empty — click Add a block below.</p>
          )}
          {hiddenCount > 0 && (
            <p className="text-xs text-center text-[#5C5C5C] italic py-2">
              {hiddenCount} hidden block{hiddenCount === 1 ? "" : "s"} shown here for editing — they don&apos;t show on the public page.
            </p>
          )}
        </div>
      )}

      {/* Admin toolbar */}
      {isAdmin && !editingBlock && !showAdd && (
        <div className="fixed bottom-5 left-5 z-[900] flex flex-col items-start gap-2" data-testid="page-editor-floating">
          {editing && (
            <Button
              onClick={() => setShowAdd(true)}
              className="rounded-full bg-[#E76F51] hover:bg-[#d65f42] text-white font-semibold shadow-xl"
              data-testid="page-editor-add-block-floating"
            >
              <Plus className="h-4 w-4 mr-2" /> Add a block
            </Button>
          )}
          <Button
            onClick={() => setEditing(!editing)}
            size="lg"
            className={`rounded-full font-bold shadow-2xl text-base px-6 ${editing ? "bg-[#2E5C55] hover:bg-[#254b45] text-white" : "bg-[#E76F51] hover:bg-[#d65f42] text-white ring-4 ring-white"}`}
            data-testid="page-editor-toggle-floating"
          >
            {editing ? <Check className="h-4 w-4 mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
            {editing ? "Done editing" : "Edit this page"}
          </Button>
        </div>
      )}
      {isAdmin && (
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3 justify-center border-t border-[#E2E0DC] mt-8"
          data-testid="page-section-editor-toolbar">
          <Button
            onClick={() => setEditing(!editing)}
            className={`rounded-full font-semibold ${editing ? "bg-[#2E5C55] hover:bg-[#254b45] text-white" : "bg-white text-[#1A1A1A] border border-[#E2E0DC] hover:border-[#E76F51]"}`}
            variant={editing ? "default" : "outline"}
            data-testid="page-editor-toggle"
          >
            {editing ? <Check className="h-4 w-4 mr-2" /> : <Move className="h-4 w-4 mr-2" />}
            {editing ? "Done editing" : "Edit page sections"}
          </Button>
          {editing && (
            <Button
              onClick={() => setShowAdd(true)}
              className="rounded-full bg-[#E76F51] hover:bg-[#d65f42] text-white font-semibold"
              data-testid="page-editor-add-block"
            >
              <Plus className="h-4 w-4 mr-2" /> Add a block
            </Button>
          )}
        </div>
      )}

      {/* Add-block sheet */}
      <EditorSheet open={showAdd} onClose={() => !busy && setShowAdd(false)} title="Add a block" testId="add-block-dialog">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            {BLOCK_TYPE_KEYS.map((k) => {
              const meta = BLOCK_REGISTRY[k];
              const Icon = LucideIcons[meta.icon] || LucideIcons.Square;
              return (
                <button
                  key={k}
                  onClick={() => addBlock(k)}
                  disabled={busy}
                  className="text-left border border-[#E2E0DC] rounded-lg p-4 hover:border-[#E76F51] hover:bg-[#E76F51]/5 transition-colors"
                  data-testid={`add-block-${k}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="h-5 w-5 text-[#E76F51] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#1A1A1A] m-0">{meta.label}</p>
                      {meta.hint && <p className="text-xs text-[#5C5C5C] m-0 mt-1">{meta.hint}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
      </EditorSheet>

      {/* Edit-block sheet — the whole overlay scrolls (wheel/touch/scrollbar anywhere); header + Done stay pinned. */}
      <EditorSheet
        open={!!editingBlock}
        onClose={closeEditor}
        title={<>Edit block · {editingBlock && BLOCK_REGISTRY[editingBlock.type]?.label}</>}
        testId="block-editor-dialog"
        footer={<>
          <span className="text-xs text-[#5C5C5C]">Changes save as you type.</span>
          <Button onClick={closeEditor} className="bg-[#2E5C55] hover:bg-[#254b45]" data-testid="block-editor-done">Done</Button>
        </>}
      >
        {editingBlock && (
          <BlockEditor
            block={editingBlock}
            layoutKey={layoutKey}
            onChange={(next) => saveBlock(next)}
          />
        )}
      </EditorSheet>
    </>
  );
};

export default PageSectionEditor;
